// ─── API Client — todas las operaciones de BD van por el VPS API ─────────────

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:0542';

const getToken = async () => {
	if (process.env.REACT_APP_DEV_BYPASS === 'true') return 'dev-bypass-token';
	const { userManager } = await import('../../context/AuthContext');
	let oidcUser = await userManager.getUser();

	if (!oidcUser) return null;

	if (oidcUser.expired) {
		try {
			oidcUser = await userManager.signinSilent();
		} catch {
			// Silent renew failed — let the 401 propagate so the UI can redirect to login
			return null;
		}
	}

	return oidcUser?.access_token || null;
};

const apiFetch = async (method, path, body = null) => {
	const token = await getToken();
	const opts = { method, headers: { 'Content-Type': 'application/json' } };
	if (token) opts.headers['Authorization'] = `Bearer ${token}`;
	if (body) opts.body = JSON.stringify(body);
	const res = await fetch(`${API_URL}/api${path}`, opts);
	if (!res.ok) {
		const msg = await res.text().catch(() => res.statusText);
		throw new Error(`API ${res.status}: ${msg}`);
	}
	return res.json();
};

// ─── LECTURA ──────────────────────────────────────────────────────────────────

/**
 * Retorna array de valores de una colección completa.
 * Equivalente a: Object.values( snapshot.val() )
 */
export const getCollections = async (collectionPath) => {
	const { data } = await apiFetch('GET', `/read?path=${encodeURIComponent(collectionPath)}`);
	if (!data) return [];
	if (Array.isArray(data)) return data;
	return Object.values(data);
};

/**
 * Retorna el objeto crudo de la BD (keyed by Firebase key).
 * Equivalente a: snapshot.val()
 */
export const getCollectionsComplete = async (collectionPath) => {
	const { data } = await apiFetch('GET', `/read?path=${encodeURIComponent(collectionPath)}`);
	return data || {};
};

/**
 * Busca un documento por su key.
 */
export const searchData = async (collection, id) => {
	try {
		const { data } = await apiFetch('GET', `/read?path=${encodeURIComponent(`${collection}/${id}`)}`);
		return data || null;
	} catch {
		return null;
	}
};

/**
 * Busca el primer documento cuyo campo `key` sea igual a `value`.
 */
export const searchDataByValue = async (collection, key, value) => {
	const { data } = await apiFetch(
		'GET',
		`/read?path=${encodeURIComponent(collection)}&orderBy=${encodeURIComponent(key)}&equalTo=${encodeURIComponent(value)}`
	);
	if (!data) return null;
	const values = Object.values(data);
	return values.length > 0 ? values[0] : null;
};

// ─── ESCRITURA ────────────────────────────────────────────────────────────────

/** Sobreescribe un documento en collection/id */
export const setData = async (collection, id, json) => {
	await apiFetch('POST', '/write', { path: `${collection}/${id}`, data: json, mode: 'set' });
};

/** Actualiza campos de un documento en collection/id */
export const updateData = async (collection, id, json) => {
	await apiFetch('POST', '/write', { path: `${collection}/${id}`, data: json, mode: 'update' });
};

/** Actualiza múltiples campos directamente en una ruta */
export const updateAll = async (path, json) => {
	await apiFetch('POST', '/write', { path, data: json, mode: 'update' });
};

/** Elimina un documento */
export const deleteData = async (collection, id) => {
	await apiFetch('POST', '/write', { path: `${collection}/${id}`, mode: 'remove' });
};

/**
 * Hace push a una colección (Firebase genera el key).
 * Retorna el key generado.
 */
export const pushData = async (path, json) => {
	const { id } = await apiFetch('POST', '/write', { path, data: json, mode: 'push' });
	return id;
};

export const logEvent = async (action, metadata = {}, type = 'write', severity = 'INFO') => {
	apiFetch('POST', '/log', { type, action, severity, metadata }).catch(() => {});
};

/**
 * Suscripción simple via polling.
 * Llama a `callback(data)` inmediatamente y luego cada `intervalMs` ms.
 * Retorna una función `unsubscribe()` para detener el polling.
 */
export const listenData = (collectionPath, callback, intervalMs = 10000) => {
	let active = true;

	const fetch_ = async () => {
		if (!active) return;
		try {
			const { data } = await apiFetch('GET', `/read?path=${encodeURIComponent(collectionPath)}`);
			if (active) callback(data);
		} catch (e) {
			console.error('listenData error:', e);
		}
	};

	fetch_(); // llamada inmediata
	const timer = setInterval(fetch_, intervalMs);

	return () => {
		active = false;
		clearInterval(timer);
	};
};

/** @deprecated — usar listenData */
export async function childChange() { return {}; }

//--------------------------------------------------------------------------------------//
//-----------------------------FUNCIONES GENERALES--------------------------------------//
//--------------------------------------------------------------------------------------//

function quitarAcentos(cadena) {
	const acentos = {
		á: 'a',
		é: 'e',
		í: 'i',
		ó: 'o',
		ú: 'u',
		Á: 'A',
		É: 'E',
		Í: 'I',
		Ó: 'O',
		Ú: 'U',
		Ñ: 'n',
		ñ: 'n',
	};
	return cadena
		.split('')
		.map((letra) => acentos[letra] || letra)
		.join('')
		.toString();
}

export function cambiarTextos(string) {
	const minusculas = string.toLowerCase();
	const sinEspacios = minusculas.replace(' ', '-');
	const sinTildes = quitarAcentos(sinEspacios);
	return sinTildes;
}

export function stringToArray(string, separador = ',') {
	const array = string.split(separador);
	return array;
}

export function capitalize(string) {
	const resultado = string.charAt(0).toUpperCase() + string.slice(1);
	return resultado;
}

export function cualMes(id) {
	let mes = '';
	switch (id) {
		case '01':
			mes = 'Enero';
			break;
		case '02':
			mes = 'Febrero';
			break;
		case '03':
			mes = 'Marzo';
			break;
		case '04':
			mes = 'Abril';
			break;
		case '05':
			mes = 'Mayo';
			break;
		case '06':
			mes = 'Junio';
			break;
		case '07':
			mes = 'Julio';
			break;
		case '08':
			mes = 'Agosto';
			break;
		case '09':
			mes = 'Septiembre';
			break;
		case '10':
			mes = 'Octubre';
			break;
		case '11':
			mes = 'Noviembre';
			break;
		case '12':
			mes = 'Diciembre';
			break;

		default:
			break;
	}
	return mes;
	// let mes = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
	// return mes[id];
}

export function getDate() {
	const today = new Date();
	const year = today.getFullYear().toString();
	let month = (today.getMonth() + 1).toString();
	let day = today.getDate().toString();
	let hours = today.getHours();
	let minutes = today.getMinutes();
	let seconds = today.getSeconds();
	const meridian = hours < 12 ? 'am' : 'pm';
	if (month.toString().length === 1) {
		month = '0' + month;
	}
	if (day.toString().length === 1) {
		day = '0' + day;
	}
	if (hours.toString().length === 1) {
		hours = '0' + hours;
	}
	if (minutes.toString().length === 1) {
		minutes = '0' + minutes;
	}
	if (seconds.toString().length === 1) {
		seconds = '0' + seconds;
	}
	let date = `${year}-${month}-${day} ${hours}:${minutes}:${seconds} ${meridian}`;
	return date;
}

export function validateEmail(email) {
	const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	return re.test(email);
}

export function setFavicons(favImg) {
	const headTitle = document.querySelector('head');
	const setFavicon = document.createElement('link');
	setFavicon.setAttribute('rel', 'shortcut icon');
	setFavicon.setAttribute('href', favImg);
	headTitle.appendChild(setFavicon);
}

export async function downloadFile(file, name) {
	try {
		const response = await fetch(file);
		const blob = await response.blob(); // Obtener la imagen como un Blob

		// Crear una URL de objeto a partir del Blob
		const url = window.URL.createObjectURL(blob);

		// Crear un elemento <a> temporal para la descarga
		const link = document.createElement('a');
		link.href = url;
		link.download = name;

		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		// Liberar la URL del objeto una vez que ya no se necesita
		window.URL.revokeObjectURL(url);
	} catch (error) {
		console.error('Error al descargar la imagen:', error);
		alert('Hubo un error al intentar descargar la imagen. Verifica la consola para más detalles.');
	}

}

export function top() {
	window.scrollTo(0, 0);
}

export function shuffle(array) {
	return array.sort((a, b) => 0.5 - Math.random());
}

export async function userActive() {
	// return await getCollectionsComplete(`${process.env.REACT_APP_BD_USUARIOS}/${sessionStorage.getItem(`${process.env.REACT_APP_VARIABLE_SESION_STORAGE}`)}`);
	const userData = JSON.parse(sessionStorage.getItem(process.env.REACT_APP_VARIABLE_SESION_STORAGE));
	return userData;
}

export const buscarUsuarioPorCorreo = async (correo) => {
	const { data } = await apiFetch(
		'GET',
		`/read?path=usuarios&orderBy=correo&equalTo=${encodeURIComponent(correo)}`
	);
	if (!data) return null;
	const entries = Object.entries(data);
	return entries.length > 0 ? entries[0][0] : null; // retorna el key
};

// Deteccion de browser
export function browser() {
	let dispositivo = navigator.userAgent;
	let browser = 'Desconocido';

	switch (true) {
		//	Microsoft Edge
		case /Edge/.test(dispositivo):
			browser = 'Edge';
			break;
		//	Mozilla Firefox
		case /Firefox/.test(dispositivo):
			browser = 'Firefox';
			break;
		//	Opera
		case /OPR/.test(dispositivo):
			browser = 'Opera';
			break;
		// Google Chrome
		case /Chrome/.test(dispositivo) && /Google Inc/.test(navigator.vendor):
			browser = 'Chrome';
			break;
		// Google Chrome Web
		case dispositivo.includes('Chrome') && !dispositivo.includes('Android'):
			browser = 'ChromeWeb';
			break;
		//	Google Chrome Movil
		case dispositivo.includes('Chrome') && dispositivo.includes('Android'):
			browser = 'ChromeMovil';
			break;
		//	Safari Mac
		case /Safari/.test(dispositivo) && /Apple Computer/.test(navigator.vendor):
			browser = 'SafariWeb';
			break;
		// Internet Explorer
		case /MSIE/.test(dispositivo) || /Trident/.test(dispositivo):
			browser = 'Explorer';
			break;
		// Opera Mini
		case /Opera Mini/.test(dispositivo):
			browser = 'OperaMini';
			break;
		//	Google Chrome (iOS)
		case /CriOS/.test(dispositivo):
			browser = 'ChromeiOS';
			break;
		//	Firefox (iOS)
		case /FxiOS/.test(dispositivo):
			browser = 'FirefoxiOS';
			break;
		// Safari iOS
		case /iPhone/.test(dispositivo) && /Safari/.test(dispositivo):
			browser = 'SafariiOS';
			break;
		// Android
		case /android/i.test(dispositivo):
			browser = 'Android';
			break;
		// Dispositivos iOS
		case /iphone|ipad|ipod/i.test(dispositivo):
			browser = 'iOS';
			break;
	}
	return browser;
}

export function isMobile() {
	let isMobile = false;
	if (
		navigator.userAgent.match(/Android/i) ||
		navigator.userAgent.match(/webOS/i) ||
		navigator.userAgent.match(/iPhone/i) ||
		navigator.userAgent.match(/iPad/i) ||
		navigator.userAgent.match(/iPod/i) ||
		navigator.userAgent.match(/BlackBerry/i) ||
		navigator.userAgent.match(/Windows Phone/i)
	) {
		isMobile = true;
	}
	return isMobile;
}

export function validarFechas(fecha) {
	let pasa = false;
	const today = new Date();
	const year = today.getFullYear().toString();
	let month = (today.getMonth() + 1).toString();
	let day = today.getDate().toString();
	if (month.toString().length === 1) {
		month = '0' + month;
	}
	if (day.toString().length === 1) {
		day = '0' + day;
	}

	let fechaActual = `${year}-${month}-${day}`;
	if (fechaActual > fecha) {
		pasa = false;
	} else {
		pasa = true;
	}
	return pasa;
}

//--------------------------------------------------------------------------------------//
//-----------------------------FUNCIONES DE FIREBASE AUTH------------------------------//
//--------------------------------------------------------------------------------------//

/**
 * Elimina un usuario tanto de Firebase Auth como de la base de datos
 * @param {string} uid - UID del usuario a eliminar
 * @returns {Promise} - Resultado de la operación
 */
export const eliminarUsuarioFirebaseAuth = async (uid) => {
	console.log('Eliminando usuario con UID:', uid);
	try {
		const { userManager } = await import('../../context/AuthContext');
		const oidcUser = await userManager.getUser();

		if (!oidcUser) {
			throw new Error('Usuario no autenticado');
		}

		const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:0542';
		const response = await fetch(`${API_URL}/api/usuarios/${uid}`, {
			method: 'DELETE',
			headers: {
				'Authorization': `Bearer ${oidcUser.access_token}`,
			},
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
			throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
		}

		const result = await response.json();
		console.log('Usuario eliminado exitosamente:', result);
		return result;
	} catch (error) {
		console.error('Error eliminando usuario:', error);
		if (error.message.includes('Failed to fetch')) {
			throw new Error('Error de conexión. Verifica que el API esté corriendo.');
		}
		throw error;
	}
};
